/**
 * TypeStone — a slowly rotating 3D rounded cube ("stone without sharp
 * corners") whose surface takes the colour of the user's determined type.
 * Renders in a fixed 260×260 canvas with subtle floor + glow.
 */
import { Canvas } from "@react-three/fiber";
import { RoundedBox, Float, Environment } from "@react-three/drei";
import { Suspense } from "react";

function Stone({ color = "#A066FF" }) {
  return (
    <Float speed={1.4} rotationIntensity={0.9} floatIntensity={0.8}>
      <group>
        {/* Core rounded block */}
        <RoundedBox args={[1.55, 1.55, 1.55]} radius={0.42} smoothness={8} castShadow>
          <meshPhysicalMaterial
            color={color}
            metalness={0.35}
            roughness={0.28}
            clearcoat={0.9}
            clearcoatRoughness={0.15}
            envMapIntensity={1.15}
            reflectivity={0.7}
          />
        </RoundedBox>
        {/* Subtle inner highlight cube */}
        <RoundedBox args={[1.35, 1.35, 1.35]} radius={0.38} smoothness={6}>
          <meshBasicMaterial color={color} transparent opacity={0.08} />
        </RoundedBox>
      </group>
    </Float>
  );
}

export default function TypeStone({ color = "#A066FF", size = 260 }) {
  return (
    <div
      data-testid="type-stone"
      style={{
        width: size,
        height: size,
        margin: "0 auto",
        filter: `drop-shadow(0 0 40px ${color}55)`,
      }}
    >
      <Canvas
        camera={{ position: [0, 0, 3.6], fov: 40 }}
        dpr={[1, 2]}
        gl={{ antialias: true, alpha: true }}
      >
        <ambientLight intensity={0.55} />
        <pointLight position={[3, 3, 3]} intensity={1.4} color={color} />
        <pointLight position={[-2, -2, 2]} intensity={0.9} color="#ffffff" />
        <Suspense fallback={null}>
          <Environment preset="night" />
          <Stone color={color} />
        </Suspense>
      </Canvas>
    </div>
  );
}
